import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControlLabel,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  Radio,
  RadioGroup,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  DataGrid,
  GridToolbarContainer,
  gridClasses,
  useGridApiRef,
} from "@mui/x-data-grid";
import dayjs from "dayjs";
import linguistApi, { waitForLinguistAuthUser } from "services/linguistApi";
import { auth } from "../../firebase/firebase";
import CustomDateSelector from "components/CustomDateSelector";
import DataGridHeader from "components/DataGridHeader";
import CEQuickFilter from "components/CEQuickFilter";
import {
  getDateProximityColor,
  getStatusColor,
  numericComparator,
} from "helpers/ServiceHelpers";
import { NAVBAR_HEIGHT } from "constants/layout";
import { FILTER_SELECTOR_WIDTH } from "constants/filterSelectors";
import { CONST } from "constants/CONST";
import ColumnDateFilter from "components/ColumnDateFilter";
import { useAuth } from "contexts/AuthContext";
import ColumnFilter from "components/ColumnFilter";
import { HtmlTooltip } from "components/HeatMap";
import ExcelJS from "exceljs";
import useColumnFilters from "hooks/useColumnFilters";
import { useStateContext } from "contexts/ContextProvider";
import MoreHorizRoundedIcon from "@mui/icons-material/MoreHorizRounded";
import ClearIcon from "@mui/icons-material/ClearRounded";
import MoreVertRoundedIcon from "@mui/icons-material/MoreVertRounded";
import DensityMediumIcon from "@mui/icons-material/DensityMediumRounded";
import ViewWeekRoundedIcon from "@mui/icons-material/ViewWeekRounded";
import GetAppRoundedIcon from "@mui/icons-material/GetAppRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";

const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const isInterpretationService = (item) => {
  if (String(item?.ServiceCategory || "").toLowerCase() === "interpretation")
    return true;
  return Boolean(item?.InterpretationStatus);
};

const formatServiceTime = (value) => {
  if (!value || value === "00:00:00") return "N/A";
  return dayjs.tz(value, "HH:mm:ss", "America/New_York").format("h:mm A");
};

const LinguistInterpretations = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = !useMediaQuery("(min-width:600px)");
  const [assignments, setAssignments] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const currentYear = dayjs().year();
  const currentMonth = dayjs().format("MMM");
  const [selectedYears, setSelectedYears] = useState([currentYear]);
  const [selectedMonths, setSelectedMonths] = useState([currentMonth]);
  const authRetryCountRef = useRef(0);
  const [periods, setPeriods] = useState({ years: [], monthsByYear: {} });

  const { currentUser } = useAuth();

  const { openDialog, showSnackbar, closeDialog } = useStateContext();

  const listOfServices = useMemo(
    () => assignments.filter(isInterpretationService),
    [assignments],
  );

  const [openFilter, setOpenFilter] = useState(null);

  const handleSortModelChange = (newSortModel) => {
    setSortModel(newSortModel);
    localStorage.setItem(
      CONST.INTERPRETATION_LINGUIST_SORT_KEY,
      JSON.stringify(newSortModel),
    );
  };

  const loadInterpretations = useCallback(async () => {
    setError("");
    setLoading(true);
    try {
      const params = {};
      if (Array.isArray(selectedYears) && selectedYears.length > 0) {
        params.year = selectedYears[0];
      }
      if (Array.isArray(selectedMonths) && selectedMonths.length > 0) {
        params.months = selectedMonths;
      }

      const response = await linguistApi.get("/me/assignments", { params });
      const interpretations = (response.data || []).filter(
        isInterpretationService,
      );
      setAssignments(interpretations);
      authRetryCountRef.current = 0;
    } catch (requestError) {
      const code = String(requestError?.code || "");
      if (
        code === "LINGUIST_AUTH_USER_UNAVAILABLE" ||
        code === "LINGUIST_AUTH_TOKEN_UNAVAILABLE"
      ) {
        const currentUser =
          auth.currentUser || (await waitForLinguistAuthUser(2500));
        if (!currentUser) {
          setError(
            "Your session could not be verified right now. Please sign in again if this continues.",
          );
          return;
        }

        if (authRetryCountRef.current < 2) {
          authRetryCountRef.current += 1;
          setTimeout(() => {
            loadInterpretations();
          }, 700);
          return;
        }

        setError(
          "We are still finalizing your secure session. Please refresh this page once.",
        );
        return;
      }

      const status = Number(requestError?.response?.status || 0);
      if (status === 401 || status === 403) {
        const currentUser =
          auth.currentUser || (await waitForLinguistAuthUser(5000));
        if (!currentUser) {
          setError(
            "Your session could not be verified right now. Please sign in again if this continues.",
          );
          return;
        }

        setError("We could not verify your session right now. Please retry.");
      } else {
        setError(
          requestError?.response?.data?.error ||
            "Unable to load interpretation assignments right now.",
        );
      }
    } finally {
      setLoading(false);
    }
  }, [selectedYears, selectedMonths]);

  useEffect(() => {
    loadInterpretations();
  }, [loadInterpretations]);

  // Fetch available years/months for the current linguist
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await linguistApi.get("/me/assignment-periods");
        if (cancelled) return;
        const data = resp.data || { years: [], monthsByYear: {} };
        setPeriods({ years: data.years || [], monthsByYear: data.monthsByYear || {} });

        // ensure selected year/months are valid
        if (data.years && data.years.length > 0) {
          const current = selectedYears?.[0];
          if (!data.years.includes(current)) {
            const nextYear = data.years[0];
            setSelectedYears([nextYear]);
            const monthsForYear = (data.monthsByYear && data.monthsByYear[nextYear]) || [currentMonth];
            setSelectedMonths(monthsForYear.length > 0 ? monthsForYear : [currentMonth]);
          }
        }
      } catch (err) {
        console.warn("/me/assignment-periods failed:", err?.message || err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const availableYears = useMemo(() => {
    if (periods.years && periods.years.length > 0) return periods.years;
    const years = new Set(
      assignments
        .map((item) => dayjs(item.Date).year())
        .filter((year) => Number.isFinite(year)),
    );
    const asArray = Array.from(years).sort((a, b) => b - a);
    if (asArray.length === 0) return [dayjs().year()];
    return asArray;
  }, [assignments, periods.years]);

  const handleDateSelectionSave = useCallback(
    (newSelection) => {
      const nextMonths = Array.isArray(newSelection?.months)
        ? newSelection.months
        : [];
      const nextYears = Array.isArray(newSelection?.years)
        ? newSelection.years
        : [];

      setSelectedMonths(nextMonths.length > 0 ? nextMonths : [currentMonth]);
      setSelectedYears(
        nextYears.length > 0
          ? [nextYears[nextYears.length - 1]]
          : [currentYear],
      );
    },
    [currentMonth, currentYear],
  );

  const filteredItems = useMemo(() => {
    const selectedYear = selectedYears?.[0] || currentYear;
    const selectedMonthIndices = (selectedMonths || [])
      .map((monthName) => dayjs().month(monthName).month())
      .filter((value) => Number.isFinite(value) && value >= 0 && value <= 11);

    return assignments.filter((item) => {
      if (!item?.Date) return false;

      const itemDate = dayjs(item.Date);
      if (!itemDate.isValid()) return false;
      if (String(itemDate.year()) !== String(selectedYear)) return false;
      if (
        selectedMonthIndices.length > 0 &&
        !selectedMonthIndices.includes(itemDate.month())
      )
        return false;
      return true;
    });
  }, [assignments, selectedYears, selectedMonths, currentYear]);

  const rows = useMemo(
    () =>
      filteredItems.map((item) => ({
        id: item.id,
        ProjectNumber: item.ProjectNumber || "N/A",
        Date: item.Date ? dayjs(item.Date).format("MMM D, YYYY") : "N/A",
        TimeStart: formatServiceTime(item.TimeStart),
        TimeEnd: formatServiceTime(item.TimeEnd),
        InterpretationStatus: item.InterpretationStatus || "Assigned",
        ServiceName: item.Name || `Interpretation #${item.id}`,
        Details: item.Details || "N/A",
      })),
    [filteredItems],
  );

  const getStoredFilterModel = () => {
    const stored = localStorage.getItem(CONST.INTERPRETATION_LINGUIST_FILTER_KEY);
    return stored ? JSON.parse(stored) : { items: [] };
  };

  const [filterModel, setFilterModel] = useState(getStoredFilterModel);

  const hasQuickFilter = filterModel?.quickFilterValues?.length > 0;

  const CustomToolbar = useMemo(() => {
    const Toolbar = ({
      hasAnyActiveFilters: toolbarHasFilters,
      onClear,
      theme: toolbarTheme,
      //selectionEnabled: toolbarSelectionEnabled,
      //selectionModel: toolbarSelectionModel,
      //allSelectedChecked: toolbarAllSelectedChecked,
      //onCancelSelection,
      //onMarkChecked,
      //onAddToCalendar,
    }) => {
      const [anchorEl, setAnchorEl] = useState(null);
      const open = Boolean(anchorEl);

      const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);
      const optionsOpen = Boolean(optionsAnchorEl);

      const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
      };

      const handleClose = () => {
        setAnchorEl(null);
      };

      const handleOptionsClick = (event) => {
        setOptionsAnchorEl(event.currentTarget);
      };

      const handleOptionsClose = () => {
        setOptionsAnchorEl(null);
      };

      const buttons = [];

      return (
        <GridToolbarContainer
          sx={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            gap: 0,
          }}
        >
          {isMobile && buttons.length > 0 && (
            <>
              <Tooltip title="Actions">
                <IconButton
                  onClick={handleClick}
                  size="small"
                  sx={{
                    mx: 1,
                    color: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    border: `1px solid ${theme.palette.primary.main}`,
                    borderRadius: "8px",
                    "&:hover": {
                      borderColor: theme.palette.primary.buttonHoverSelected,
                      backgroundColor:
                        theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.default,
                    },
                  }}
                >
                  <MoreHorizRoundedIcon />
                </IconButton>
              </Tooltip>
              <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <Box sx={{ minWidth: 200 }}>
                  {buttons.map((btn, index) => (
                    <MenuItem
                      key={index}
                      onClick={btn.onClick}
                      disabled={btn.disabled}
                      sx={{
                        color: btn.isChecked
                          ? theme.palette.text.disabled
                          : theme.palette.primary.main,
                      }}
                    >
                      {btn.icon && (
                        <ListItemIcon
                          sx={{
                            color: btn.isChecked
                              ? theme.palette.text.disabled
                              : theme.palette.primary.main,
                          }}
                        >
                          {btn.icon}
                        </ListItemIcon>
                      )}
                      <ListItemText>{btn.label}</ListItemText>
                    </MenuItem>
                  ))}
                </Box>
              </Popover>
            </>
          )}

          {!isMobile && (
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                alignItems: "center",
              }}
            >
              {toolbarHasFilters && (
                <Button
                  startIcon={<ClearIcon />}
                  onClick={onClear}
                  variant="outlined"
                  size="small"
                  sx={{
                    color: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                    "&:hover": {
                      borderColor: theme.palette.primary.buttonHoverSelected,
                      backgroundColor:
                        theme.palette.primary.buttonHoverSelected,
                      color: theme.palette.background.alt,
                    },
                  }}
                >
                  Clear Filters
                </Button>
              )}
            </Box>
          )}

          {isMobile && toolbarHasFilters && (
            <Tooltip title="Clear Filters">
              <IconButton
                onClick={onClear}
                size="small"
                sx={{
                  color: theme.palette.primary.main,
                  borderColor: theme.palette.primary.main,
                  border: `1px solid ${theme.palette.primary.main}`,
                  borderRadius: "8px",
                  "&:hover": {
                    borderColor: theme.palette.primary.buttonHoverSelected,
                    backgroundColor: theme.palette.primary.buttonHoverSelected,
                    color: theme.palette.background.default,
                  },
                }}
              >
                <ClearIcon />
              </IconButton>
            </Tooltip>
          )}

          <Box sx={{ flex: 1 }} />

          <Box
            sx={{
              flex: 1,
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "flex-start",
              gap: 1,
            }}
          >
            <CEQuickFilter iconOnly={isMobile} />

            <Tooltip title="Grid Options">
              <IconButton
                onClick={handleOptionsClick}
                size="small"
                sx={{
                  height: 36,
                  width: 36,
                  color: toolbarTheme.palette.grey.main,
                  borderColor: toolbarTheme.palette.divider,
                  border: `1px solid ${toolbarTheme.palette.divider}`,
                  borderRadius: "12px",
                  "&:hover": {
                    borderColor: toolbarTheme.palette.primary.main,
                    //backgroundColor: toolbarTheme.palette.primary.buttonHoverSelected,
                    color: toolbarTheme.palette.primary.main,
                  },
                }}
              >
                <MoreVertRoundedIcon />
              </IconButton>
            </Tooltip>

            <Menu
              anchorEl={optionsAnchorEl}
              open={optionsOpen}
              onClose={handleOptionsClose}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <MenuItem
                onClick={() => {
                  setOverlayMode("density");
                  setOverlayOpen(true);
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <DensityMediumIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Density</ListItemText>
              </MenuItem>

              <MenuItem
                onClick={() => {
                  setOverlayMode("columns");
                  setOverlayOpen(true);
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <ViewWeekRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Columns</ListItemText>
              </MenuItem>

              <Divider />

              <MenuItem
                onClick={() => {
                  openDialog({
                    title: "Export Data",
                    theme: theme,
                    customContent: <ExportDialogContent />,
                  });
                  handleOptionsClose();
                }}
              >
                <ListItemIcon>
                  <GetAppRoundedIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>Export</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </GridToolbarContainer>
      );
    };

    return Toolbar;
  }, [theme, isMobile, openDialog, currentUser?.Role, currentUser?.role]);

  const getSavedColumnsVisible = () => {
    const saved = localStorage.getItem("INTERPRETATIONS_LINGUIST_COLUMNS_VISIBLE");
    const parsed = saved ? JSON.parse(saved) : {};

    // Keep core cost columns visible to avoid stale hidden preferences
    // masking newly available backend fields.
    return {
      ...parsed,
      LinguistPay: true,
      TotalLinguistPay: true,
      AdditionalCost: true,
      MileageCost: true,
      ParkingFeesTolls: true,
      Address: true,
    };
  };

  const [columnsVisible, setColumnsVisible] = useState(getSavedColumnsVisible);

  // Helper function to generate localStorage key for column filters
  const getFilterStorageKey = useCallback((fieldName) => {
    // Convert field name to uppercase and create key
    // e.g., "Client" -> "INTERPRETATIONS_FILTER_CLIENT"
    const upperFieldName = fieldName.toUpperCase();
    return `INTERPRETATIONS_LINGUIST_FILTER_${upperFieldName}`;
  }, []);

  const [overlayMode, setOverlayMode] = useState(null); // 'density' or 'columns'

  // Helper function to save a column filter to localStorage
  const saveColumnFilter = useCallback(
    (fieldName, values) => {
      const key = getFilterStorageKey(fieldName);
      if (values && values.length > 0) {
        localStorage.setItem(key, JSON.stringify(values));
      } else {
        // Remove from localStorage if no values selected
        localStorage.removeItem(key);
      }
    },
    [getFilterStorageKey],
  );

  const timeComparator = (v1, v2) => {
    const format = "h:mm A"; // Matches "hh:MM a" like "1:30 PM"
    const t1 = dayjs(v1, format);
    const t2 = dayjs(v2, format);

    return t1.isBefore(t2) ? -1 : t1.isAfter(t2) ? 1 : 0;
  };

  const dateComparator = (v1, v2) => {
    if (v1 === "N/A") return 1;
    if (v2 === "N/A") return -1;
    return dayjs(v1, "M/D/YYYY").valueOf() - dayjs(v2, "M/D/YYYY").valueOf();
  };

  // Helper function to clear all column filters from localStorage
  const clearAllColumnFilters = useCallback(() => {
    const fields = [
      "ProjectNumber",
      "Date",
      "TimeStart",
      "TimeEnd",
      "Duration",
      "InterpretationStatus",
      "Address",
      "Assignment",
      "Language",
      "TotalLinguistPay",
      "LinguistPay",
      "AdditionalLinguistCost",
      "MileageRoundtrip",
      "MileageCost",
      "ActualStart",
      "ActualEnd",
      "ActualDuration",
      "Cancelled",
      "CancellationReason",
    ];

    fields.forEach((field) => {
      localStorage.removeItem(getFilterStorageKey(field));
    });
  }, [getFilterStorageKey]);

  const getStoredSortModel = () => {
    const stored = localStorage.getItem(CONST.INTERPRETATION_LINGUIST_SORT_KEY);
    return stored ? JSON.parse(stored) : [];
  };

  const getSavedDensity = () => {
    const saved = localStorage.getItem("INTERPRETATIONS_LINGUIST_GRID_DENSITY");
    return saved || "standard";
  };

  const apiRef = useGridApiRef();

  const [overlayOpen, setOverlayOpen] = useState(false);

  const [sortModel, setSortModel] = useState(getStoredSortModel);

  const prevFilteredRowIdsRef = useRef([]);

  const [filterRowIds, setFilterRowIds] = useState([]);

  const [density, setDensity] = useState(getSavedDensity);

  const columns = useMemo(
    () => [
      {
        field: "ProjectNumber",
        width: 140,
        headerName: "Project Number",
        renderHeader: () => <DataGridHeader headerName="Project #" />,
      },
      {
        field: "Date",
        headerName: "Date",
        valueGetter: (_, params) => {
          const data = params.Date;
          const value =
            data && data !== "" ? dayjs.tz(data).format("M/D/YYYY") : "N/A";
          return value;
        },
        sortComparator: dateComparator,
        minWidth: 100,
        flex: 1.0,
        renderCell: (params) => (
          <Box
            style={{ display: "flex", alignItems: "center", height: "100%" }}
          >
            <Typography
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                color: getDateProximityColor(
                  theme,
                  params.value,
                  params.row.InterpretationStatus,
                ),
              }}
            >
              {params.value}
            </Typography>
          </Box>
        ),
        renderHeader: (params) => (
          <DataGridHeader headerName={params.colDef.headerName}>
            <ColumnDateFilter
              data={filterData[1] || []}
              field={params.field}
              valueGetter={params.colDef.valueGetter}
              selectedValues={columnFilters[params.field] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage(params.field, values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === params.field)?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </DataGridHeader>
        ),
      },
      {
        field: "TimeStart",
        sortComparator: timeComparator,
        headerName: "Time Start",
        valueGetter: (_, params) => {
          const data = params.TimeStart;
          if (data === "00:00:00") {
            return "12:00 AM";
          }
          if (data && data !== "") {
            return dayjs
              .tz(data, "HH:mm:ss", "America/New_York")
              .format("h:mm A");
          }
          return "N/A";
        },
        minWidth: 125,
        flex: 1.25,
        renderHeader: (params) => (
          <DataGridHeader headerName={params.colDef.headerName}>
            <ColumnFilter
              data={filterData[4] || []}
              field={params.field}
              valueGetter={params.colDef.valueGetter}
              selectedValues={columnFilters[params.field] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage(params.field, values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === params.field)?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </DataGridHeader>
        ),
      },
      {
        field: "TimeEnd",
        sortComparator: timeComparator,
        headerName: "Time End",
        valueGetter: (_, params) => {
          const data = params.TimeEnd;
          if (data === "00:00:00") {
            return "12:00 AM";
          }
          if (data && data !== "") {
            return dayjs
              .tz(data, "HH:mm:ss", "America/New_York")
              .format("h:mm A");
          }
          return "N/A";
        },
        minWidth: 120,
        flex: 1.2,
        renderHeader: (params) => (
          <DataGridHeader headerName={params.colDef.headerName}>
            <ColumnFilter
              data={filterData[5] || []}
              field={params.field}
              valueGetter={params.colDef.valueGetter}
              selectedValues={columnFilters[params.field] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage(params.field, values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === params.field)?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </DataGridHeader>
        ),
      },
      {
        field: "Duration",
        headerName: "Duration",
        valueGetter: (_, params) => {
          const data = params.Duration;
          let value = "N/A";

          if (data && data !== "00:00:00") {
            const [hours, minutes] = data.split(":");
            value = `${hours}:${minutes}`;
          }

          return value;
        },
        minWidth: 115,
        flex: 1.15,
        renderHeader: (params) => (
          <DataGridHeader headerName={params.colDef.headerName}>
            <ColumnFilter
              data={listOfServices || []}
              field={params.field}
              valueGetter={params.colDef.valueGetter}
              selectedValues={columnFilters[params.field] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage(params.field, values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === params.field)?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </DataGridHeader>
        ),
      },
      {
        field: "InterpretationStatus",
        headerName: "Status",
        minWidth: 120,
        flex: 1.2,
        renderCell: (params) => (
          <Box
            style={{ display: "flex", alignItems: "center", height: "100%" }}
          >
            <HtmlTooltip
              title={
                <Typography sx={{ whiteSpace: "pre-wrap" }}>
                  {params.value}
                </Typography>
              }
            >
              <Box
                sx={{
                  borderRadius: "8px",
                  px: "0.5rem",
                  backgroundColor: getStatusColor(theme, params.value),
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: theme.palette.background.default,
                  }}
                >
                  {params.value}
                </Typography>
              </Box>
            </HtmlTooltip>
          </Box>
        ),
        renderHeader: (params) => (
          <DataGridHeader headerName={params.colDef.headerName}>
            <ColumnFilter
              data={listOfServices || []}
              field={params.field}
              selectedValues={columnFilters[params.field] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage(params.field, values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === params.field)?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </DataGridHeader>
        ),
      },
      {
        field: "Address",
        headerName: "Address",
        valueGetter: (_, params) => {
          const data = params.Address;
          const value = data && data !== "" ? data : "N/A";
          return value;
        },
        renderCell: (params) => (
          <Box
            style={{ display: "flex", alignItems: "center", height: "100%" }}
          >
            <Typography
              sx={{
                display: "-webkit-box",
                overflow: "hidden",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 2,
              }}
              style={{ whiteSpace: "pre-wrap" }}
            >
              {params.value}
            </Typography>
          </Box>
        ),
        minWidth: 220,
        flex: 2.2,
        renderHeader: (params) => (
          <DataGridHeader headerName={params.colDef.headerName}>
            <ColumnFilter
              data={listOfServices || []}
              field={params.field}
              valueGetter={params.colDef.valueGetter}
              selectedValues={columnFilters[params.field] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage(params.field, values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === params.field)?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </DataGridHeader>
        ),
      },
      {
        field: "Name",
        minWidth: 220,
        flex: 1,
        headerName: "Assignment",
        renderHeader: () => <DataGridHeader headerName="Assignment" />,
      },
      {
        field: "TargetLanguage",
        headerName: "Language",
        valueGetter: (_, params) => {
          const languages = params.ServiceTargetLanguages;
          if (!languages || languages.length === 0) return "N/A";
          // Extract the Language.Name from each item and filter out any empty values
          return languages
            .map((item) =>
              item.Language && item.Language.Name ? item.Language.Name : "",
            )
            .filter((name) => name)
            .join(" | ");
        },
        renderCell: (params) => (
          <Box
            style={{ display: "flex", alignItems: "center", height: "100%" }}
          >
            <Typography
              sx={{
                display: "-webkit-box",
                overflow: "hidden",
                WebkitBoxOrient: "vertical",
                WebkitLineClamp: 2,
              }}
              style={{ whiteSpace: "pre-wrap" }}
            >
              {params.value}
            </Typography>
          </Box>
        ),
        minWidth: 120,
        flex: 1.2,
        renderHeader: (params) => (
          <DataGridHeader headerName={params.colDef.headerName}>
            <ColumnFilter
              data={listOfServices || []}
              field={params.field}
              valueGetter={params.colDef.valueGetter}
              selectedValues={columnFilters[params.field] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage(params.field, values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === params.field)?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </DataGridHeader>
        ),
      },
      {
        field: "TotalLinguistPay",
        headerName: "Total Linguist Pay",
        sortComparator: numericComparator,
        valueGetter: (_, params) => {
          const data = params.TotalLinguistPay;
          let value = "N/A";

          if (data !== undefined && data !== null) {
            value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }

          return value;
        },
        minWidth: 180,
        flex: 1.8,
        renderHeader: (params) => (
          <DataGridHeader headerName={params.colDef.headerName}>
            <ColumnFilter
              data={listOfServices || []}
              field={params.field}
              valueGetter={params.colDef.valueGetter}
              selectedValues={columnFilters[params.field] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage(params.field, values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === params.field)?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </DataGridHeader>
        ),
      },
      {
        field: "LinguistPay",
        headerName: "Linguist Pay",
        sortComparator: numericComparator,
        valueGetter: (_, params) => {
          const data = params.LinguistPay;
          let value = "N/A";

          if (data !== undefined && data !== null) {
            value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }

          return value;
        },
        minWidth: 140,
        flex: 1.4,
        renderHeader: (params) => (
          <DataGridHeader headerName={params.colDef.headerName}>
            <ColumnFilter
              data={listOfServices || []}
              field={params.field}
              valueGetter={params.colDef.valueGetter}
              selectedValues={columnFilters[params.field] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage(params.field, values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === params.field)?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </DataGridHeader>
        ),
      },
      {
        field: "AdditionalLinguistCost",
        headerName: "Additional Linguist Cost",
        sortComparator: numericComparator,
        valueGetter: (_, params) => {
          const data = params.AdditionalLinguistCost;
          let value = "N/A";

          if (data !== undefined && data !== null) {
            value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }

          return value;
        },
        minWidth: 220,
        flex: 2.2,
        renderHeader: (params) => (
          <DataGridHeader headerName={params.colDef.headerName}>
            <ColumnFilter
              data={listOfServices || []}
              field={params.field}
              valueGetter={params.colDef.valueGetter}
              selectedValues={columnFilters[params.field] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage(params.field, values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === params.field)?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </DataGridHeader>
        ),
      },
      {
        field: "MileageRoundtrip",
        headerName: "Mileage Roundtrip",
        sortComparator: numericComparator,
        valueGetter: (_, params) => {
          const data = params.MileageRoundtrip;
          let value = "N/A";

          if (data !== undefined && data !== null) {
            value = parseFloat(data) === 0 ? "N/A" : `${data}`;
          }

          return value;
        },
        minWidth: 170,
        flex: 1.7,
        renderHeader: (params) => (
          <DataGridHeader headerName={params.colDef.headerName}>
            <ColumnFilter
              data={listOfServices || []}
              field={params.field}
              valueGetter={params.colDef.valueGetter}
              selectedValues={columnFilters[params.field] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage(params.field, values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === params.field)?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </DataGridHeader>
        ),
      },
      {
        field: "MileageCost",
        headerName: "Mileage Cost",
        sortComparator: numericComparator,
        valueGetter: (_, params) => {
          const data = params.MileageCost;
          let value = "N/A";

          if (data !== undefined && data !== null) {
            value = parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }

          return value;
        },
        minWidth: 140,
        flex: 1.4,
        renderHeader: (params) => (
          <DataGridHeader headerName={params.colDef.headerName}>
            <ColumnFilter
              data={listOfServices || []}
              field={params.field}
              valueGetter={params.colDef.valueGetter}
              selectedValues={columnFilters[params.field] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage(params.field, values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === params.field)?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </DataGridHeader>
        ),
      },

      {
        field: "ActualStart",
        sortComparator: timeComparator,
        headerName: "Actual Start",
        valueGetter: (_, params) => {
          const data = params.ActualStart;
          if (data === "00:00:00") {
            return "12:00 AM";
          }
          if (data && data !== "") {
            return dayjs
              .tz(data, "HH:mm:ss", "America/New_York")
              .format("h:mm A");
          }
          return "N/A";
        },
        minWidth: 140,
        flex: 1.4,
        renderHeader: (params) => (
          <DataGridHeader headerName={params.colDef.headerName}>
            <ColumnFilter
              data={listOfServices || []}
              field={params.field}
              valueGetter={params.colDef.valueGetter}
              selectedValues={columnFilters[params.field] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage(params.field, values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === params.field)?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </DataGridHeader>
        ),
      },
      {
        field: "ActualEnd",
        sortComparator: timeComparator,
        headerName: "Actual End",
        valueGetter: (_, params) => {
          const data = params.ActualEnd;
          if (data === "00:00:00") {
            return "12:00 AM";
          }
          if (data && data !== "") {
            return dayjs
              .tz(data, "HH:mm:ss", "America/New_York")
              .format("h:mm A");
          }
          return "N/A";
        },
        minWidth: 135,
        flex: 1.35,
        renderHeader: (params) => (
          <DataGridHeader headerName={params.colDef.headerName}>
            <ColumnFilter
              data={listOfServices || []}
              field={params.field}
              valueGetter={params.colDef.valueGetter}
              selectedValues={columnFilters[params.field] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage(params.field, values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === params.field)?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </DataGridHeader>
        ),
      },
      {
        field: "TotalDuration",
        headerName: "Total Duration",
        valueGetter: (_, params) => {
          const data = params.TotalDuration;
          let value = "N/A";

          if (data && data !== "00:00:00") {
            const [hours, minutes] = data.split(":");
            value = `${hours}:${minutes}`;
          }

          return value;
        },
        minWidth: 155,
        flex: 1.55,
        renderHeader: (params) => (
          <DataGridHeader headerName={params.colDef.headerName}>
            <ColumnFilter
              data={listOfServices || []}
              field={params.field}
              valueGetter={params.colDef.valueGetter}
              selectedValues={columnFilters[params.field] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage(params.field, values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === params.field)?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </DataGridHeader>
        ),
      },
      {
        field: "Cancelled",
        headerName: "Cancelled",
        valueGetter: (_, params) => {
          const data = params.Cancelled;
          let value = "N/A";

          if (data !== undefined && data !== null) {
            value = data ? "Yes" : "No";
          }

          return value;
        },
        renderCell: (params) => {
          const data = params.row.Cancelled;
          if (data === undefined || data === null) return "N/A";
          return (
            <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
              <Box
                sx={{ display: "flex", alignItems: "center", height: "21px" }}
              >
                {data ? (
                  <CheckRoundedIcon sx={{ fontSize: "20px" }} color="success" />
                ) : (
                  <CloseRoundedIcon sx={{ fontSize: "20px" }} color="error" />
                )}
              </Box>
            </Box>
          );
        },
        minWidth: 120,
        flex: 1.2,
        renderHeader: (params) => (
          <DataGridHeader headerName={params.colDef.headerName}>
            <ColumnFilter
              data={listOfServices || []}
              field={params.field}
              valueGetter={params.colDef.valueGetter}
              selectedValues={columnFilters[params.field] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage(params.field, values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === params.field)?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </DataGridHeader>
        ),
      },
      {
        field: "CancellationReason",
        headerName: "Cancellation Reason",
        valueGetter: (_, params) => {
          const data = params.CancellationReason;
          const value = data && data !== "" ? data : "N/A";
          return value;
        },
        minWidth: 200,
        flex: 2.0,
        renderHeader: (params) => (
          <DataGridHeader headerName={params.colDef.headerName}>
            <ColumnFilter
              data={listOfServices || []}
              field={params.field}
              valueGetter={params.colDef.valueGetter}
              selectedValues={columnFilters[params.field] || []}
              onFilterChange={(values) =>
                handleColumnFilterChangeWithStorage(params.field, values)
              }
              onSortChange={(field, direction) => {
                const newSortModel = direction
                  ? [{ field, sort: direction }]
                  : [];
                setSortModel(newSortModel);
                handleSortModelChange(newSortModel);
              }}
              currentSort={
                sortModel.find((s) => s.field === params.field)?.sort || null
              }
              openFilter={openFilter}
              setOpenFilter={setOpenFilter}
            />
          </DataGridHeader>
        ),
      },
    ],
    [assignments],
  );

  const filteredColumns = columns.filter(
    (col) => columnsVisible[col.field] !== false,
  );

  const {
    filteredData: columnFilteredData,
    columnFilters,
    handleFilterChange: handleColumnFilterChange,
    clearAllFilters,
    hasActiveFilters,
  } = useColumnFilters(listOfServices || [], columns);

  // Wrapped clear all filters that also clears localStorage
  const handleClearAllFilters = useCallback(() => {
    clearAllFilters();
    clearAllColumnFilters();
    // Clear quick filter
    setFilterModel({ items: [] });
    localStorage.setItem(
      CONST.INTERPRETATION_LINGUIST_FILTER_KEY,
      JSON.stringify({ items: [] }),
    );
  }, [clearAllFilters, clearAllColumnFilters]);

  // Combined check for any active filters
  const hasAnyActiveFilters = hasActiveFilters || hasQuickFilter;

  // keep toolbar identity stable so quick filter input retains focus
  const clearAllFiltersRef = useRef(handleClearAllFilters);
  useEffect(() => {
    clearAllFiltersRef.current = handleClearAllFilters;
  }, [handleClearAllFilters]);

  // Grid Options Overlay Component
  const GridOptionsOverlay = () => (
    <Box
      sx={{
        p: 2,
        bgcolor: theme.palette.background.alt,
        borderRadius: "12px",
        boxShadow: theme.shadows[4],
        width: { xs: "calc(100vw - 2rem)", sm: "700px", md: "860px" },
        maxWidth: "calc(100vw - 2rem)",
        minWidth: 0,
      }}
    >
      {overlayMode === "density" && (
        <Box>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
              Grid Density
            </Typography>
            <IconButton
              size="small"
              onClick={() => setOverlayOpen(false)}
              sx={{
                color: theme.palette.primary.main,
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
              }}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Box>
          <RadioGroup
            value={density}
            onChange={(e) => setDensity(e.target.value)}
          >
            <FormControlLabel
              value="compact"
              control={<Radio />}
              label="Compact"
            />
            <FormControlLabel
              value="standard"
              control={<Radio />}
              label="Standard"
            />
            <FormControlLabel
              value="comfortable"
              control={<Radio />}
              label="Comfortable"
            />
          </RadioGroup>
        </Box>
      )}

      {overlayMode === "columns" && (
        <Box>
          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{
                fontWeight: 600,
                color: theme.palette.text.primary,
              }}
            >
              Columns
            </Typography>
            <Button
              size="small"
              variant="outlined"
              onClick={() => setColumnsVisible({})}
              disabled={
                !columns.some((col) => columnsVisible[col.field] === false)
              }
              sx={{
                flexShrink: 0,
                minWidth: "auto",
                px: 1,
              }}
            >
              Reset
            </Button>
          </Box>
          <Grid container spacing={0.5} rowSpacing={1.25}>
            {columns.map((col) => (
              <Grid key={col.field} size={{ xs: 12, sm: 6, md: 4 }}>
                <FormControlLabel
                  label={col.headerName}
                  sx={{
                    m: 0,
                    width: "100%",
                    py: 0.25,
                    alignItems: "center",
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.875rem",
                      lineHeight: 1.3,
                      display: "inline-flex",
                      alignItems: "center",
                    },
                    "& .MuiCheckbox-root": {
                      py: 0.5,
                    },
                  }}
                  control={
                    <Checkbox
                      size="small"
                      checked={columnsVisible[col.field] !== false}
                      onChange={(e) => {
                        setColumnsVisible((prev) => ({
                          ...prev,
                          [col.field]: e.target.checked,
                        }));
                      }}
                    />
                  }
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );

  // Filter columns configuration for cross-filtering
  const filterColumns = useMemo(
    () => [
      {
        field: "ProjectNumber",
        valueGetter: (row) =>
          row.ProjectNumber && row.ProjectNumber !== ""
            ? row.ProjectNumber
            : "N/A",
      },
      {
        field: "Date",
        valueGetter: (row) =>
          row.Date && row.Date !== ""
            ? dayjs.tz(row.Date).format("M/D/YYYY")
            : "N/A",
      },
      {
        field: "TimeStart",
        valueGetter: (row) => {
          const data = row.TimeStart;
          if (data && data !== "00:00:00") {
            return dayjs
              .tz(data, "HH:mm:ss", "America/New_York")
              .format("h:mm A");
          }
          return "N/A";
        },
      },
      {
        field: "TimeEnd",
        valueGetter: (row) => {
          const data = row.TimeEnd;
          if (data && data !== "00:00:00") {
            return dayjs
              .tz(data, "HH:mm:ss", "America/New_York")
              .format("h:mm A");
          }
          return "N/A";
        },
      },
      {
        field: "Duration",
        valueGetter: (row) => {
          const data = row.Duration;
          if (data && data !== "00:00:00") {
            const [hours, minutes] = data.split(":");
            return `${hours}:${minutes}`;
          }
          return "N/A";
        },
      },
      {
        field: "InterpretationStatus",
        valueGetter: (row) => row.InterpretationStatus || "N/A",
      },
      {
        field: "LinguistPay",
        valueGetter: (row) => {
          const data = row.LinguistPay;
          if (data !== undefined && data !== null) {
            return parseFloat(data) === 0 ? "N/A" : `$${data}`;
          }
          return "N/A";
        },
      },
      {
        field: "InterpretationType",
        valueGetter: (row) => row.ServiceSubtype?.Name || "N/A",
      },
      {
        field: "Assignment",
        valueGetter: (row) => row.Name || "N/A",
      },
    ],
    [],
  );

  const filterData = useMemo(() => {
    return filterColumns.map((column) => {
      const otherFilters = Object.entries(columnFilters).filter(
        ([field]) => field !== column.field,
      );

      if (otherFilters.length === 0) {
        return listOfServices || [];
      }

      return (listOfServices || []).filter((row) => {
        return otherFilters.every(([field, selectedValues]) => {
          if (!selectedValues || selectedValues.length === 0) return true;

          const filterColumn = filterColumns.find((col) => col.field === field);
          if (!filterColumn) return true;

          const value = filterColumn.valueGetter(row);
          return selectedValues.includes(value);
        });
      });
    });
  }, [columnFilters, listOfServices, filterColumns]);

  // Wrapped filter change handler that saves to localStorage
  const handleColumnFilterChangeWithStorage = useCallback(
    (field, values) => {
      handleColumnFilterChange(field, values);
      saveColumnFilter(field, values);
    },
    [handleColumnFilterChange, saveColumnFilter],
  );

  const handleFilterModelChange = (newFilterModel) => {
    setFilterModel(newFilterModel);
    localStorage.setItem(
      CONST.INTERPRETATION_LINGUIST_FILTER_KEY,
      JSON.stringify(newFilterModel),
    );
  };

  // Export Dialog Content Component
  const ExportDialogContent = () => (
    <Box sx={{ mt: 2 }}>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Choose export format:
      </Typography>
      <Stack direction="column" spacing={1}>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => {
            try {
              const allColumns = columns || [];
              const visibleColumns =
                apiRef.current?.getVisibleColumns?.() ??
                allColumns.filter((col) => columnsVisible[col.field] !== false);

              if (visibleColumns.length === 0) {
                console.warn("No visible columns to export");
                return;
              }

              if (!columnFilteredData || columnFilteredData.length === 0) {
                console.warn("No data available to export");
                showSnackbar(
                  "No interpretations data available to export",
                  theme,
                  "warning",
                );
                return;
              }

              const csvHeader = visibleColumns
                .map((col) => `"${col.headerName || col.field}"`)
                .join(",");

              // Use filterRowIds to get exact rows visible in DataGrid
              // If no quick filters applied (filterRowIds empty), fall back to all services
              const dataToExport =
                filterRowIds && filterRowIds.length > 0
                  ? filterRowIds
                      .map((id) => {
                        const item = listOfServices.find(
                          (item) => item.id === Number(id),
                        );
                        return item;
                      })
                      .filter(Boolean)
                  : listOfServices || [];

              // Special field mappings for nested paths
              const fieldPaths = {
                Client: "Client.Account",
                Requestor: "Requestor.Name",
                Linguist: "Linguist.Name",
                Language: "Language.Name",
                CreatedBy: "Creator.Name",
                CheckedBy: "Checker.Name",
              };

              const getFieldValue = (row, fieldPath) => {
                if (!fieldPath || !row) return "";
                const parts = fieldPath.split(".");
                let value = row;
                for (const part of parts) {
                  if (value && typeof value === "object" && part in value) {
                    value = value[part];
                  } else {
                    return "";
                  }
                }
                return value;
              };

              const csvRows = dataToExport
                .filter((row) => {
                  if (!row || typeof row !== "object") {
                    console.warn("Skipping invalid row:", row);
                    return false;
                  }
                  return true;
                })
                .map((row, rowIndex) => {
                  try {
                    return visibleColumns
                      .map((col) => {
                        try {
                          let value = "";
                          if (col.field) {
                            // Check if this field has a special mapping
                            const mappedPath = fieldPaths[col.field];
                            if (mappedPath) {
                              value = getFieldValue(row, mappedPath);
                            } else if (col.field === "TargetLanguage") {
                              // Special handling for TargetLanguage (complex nested array)
                              const languages = row.ServiceTargetLanguages;
                              if (languages && Array.isArray(languages)) {
                                value = languages
                                  .map((tl) => tl.Language?.Name || "")
                                  .join(" | ");
                              }
                            } else {
                              value = getFieldValue(row, col.field);
                              if (value === "" && col.field in row) {
                                value = row[col.field];
                              }
                            }
                          }
                          const strValue = String(value || "").replace(
                            /"/g,
                            '""',
                          );
                          return `"${strValue}"`;
                        } catch (cellError) {
                          console.warn(
                            `Error processing cell at row ${rowIndex}, column ${col.field}:`,
                            cellError.message,
                          );
                          return '""';
                        }
                      })
                      .join(",");
                  } catch (rowError) {
                    console.warn(
                      `Error processing row ${rowIndex}:`,
                      rowError.message,
                    );
                    return "";
                  }
                })
                .filter((row) => row.length > 0);

              const csvContent = [csvHeader, ...csvRows].join("\n");

              const blob = new Blob([csvContent], {
                type: "text/csv;charset=utf-8;",
              });
              const link = document.createElement("a");
              const url = URL.createObjectURL(blob);
              link.setAttribute("href", url);
              link.setAttribute(
                "download",
                `interpretations-${new Date().toISOString().split("T")[0]}.csv`,
              );
              link.style.visibility = "hidden";
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
              URL.revokeObjectURL(url);

              closeDialog();
            } catch (error) {
              console.error("Export error:", error);
            }
          }}
        >
          CSV (Custom Export)
        </Button>
        <Button
          fullWidth
          variant="outlined"
          onClick={() => {
            try {
              const allColumns = columns || [];
              const visibleColumns =
                apiRef.current?.getVisibleColumns?.() ??
                allColumns.filter((col) => columnsVisible[col.field] !== false);

              if (visibleColumns.length === 0) {
                console.warn("No visible columns to export");
                return;
              }

              if (!columnFilteredData || columnFilteredData.length === 0) {
                console.warn("No data available to export");
                showSnackbar(
                  "No interpretations data available to export",
                  theme,
                  "warning",
                );
                return;
              }

              // Use filterRowIds to get exact rows visible in DataGrid
              // If no quick filters applied (filterRowIds empty), fall back to all services

              const dataToExport =
                filterRowIds && filterRowIds.length > 0
                  ? filterRowIds
                      .map((id) => {
                        const item = listOfServices.find(
                          (item) => item.id === Number(id),
                        );
                        return item;
                      })
                      .filter(Boolean)
                  : listOfServices || [];

              // Special field mappings for nested paths
              const fieldPaths = {
                Client: "Client.Account",
                Requestor: "Requestor.Name",
                Linguist: "Linguist.Name",
                Language: "Language.Name",
                CreatedBy: "Creator.Name",
                CheckedBy: "Checker.Name",
              };

              const getFieldValue = (row, fieldPath) => {
                if (!fieldPath || !row) return "";
                const parts = fieldPath.split(".");
                let value = row;
                for (const part of parts) {
                  if (value && typeof value === "object" && part in value) {
                    value = value[part];
                  } else {
                    return "";
                  }
                }
                return value;
              };

              // Create workbook and worksheet
              const workbook = new ExcelJS.Workbook();
              const worksheet = workbook.addWorksheet("Interpretations");

              // Add header row with formatting
              const headerRow = worksheet.addRow(
                visibleColumns.map((col) => col.headerName || col.field),
              );
              headerRow.font = { bold: true };
              headerRow.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFD3D3D3" },
              };

              // Track column widths
              const columnWidths = new Array(visibleColumns.length).fill(15);

              // Add data rows and calculate widths
              dataToExport.forEach((row) => {
                if (!row || typeof row !== "object") return;

                const rowData = visibleColumns.map((col, colIndex) => {
                  try {
                    let value = "";
                    if (col.field) {
                      const mappedPath = fieldPaths[col.field];
                      if (mappedPath) {
                        value = getFieldValue(row, mappedPath);
                      } else if (col.field === "TargetLanguage") {
                        const languages = row.ServiceTargetLanguages;
                        if (languages && Array.isArray(languages)) {
                          value = languages
                            .map((tl) => tl.Language?.Name || "")
                            .join(" | ");
                        }
                      } else {
                        value = getFieldValue(row, col.field);
                        if (value === "" && col.field in row) {
                          value = row[col.field];
                        }
                      }
                    }
                    const strValue = String(value || "");
                    // Update column width based on content
                    columnWidths[colIndex] = Math.max(
                      columnWidths[colIndex],
                      Math.min(strValue.length + 2, 50),
                    );
                    return strValue;
                  } catch (error) {
                    console.warn(`Error processing cell:`, error.message);
                    return "";
                  }
                });
                worksheet.addRow(rowData);
              });

              // Apply calculated column widths
              visibleColumns.forEach((col, index) => {
                const headerLength = (col.headerName || col.field).length + 2;
                const finalWidth = Math.max(columnWidths[index], headerLength);
                worksheet.getColumn(index + 1).width = Math.min(finalWidth, 50);
              });

              // Generate and download file
              workbook.xlsx
                .writeBuffer()
                .then((buffer) => {
                  const blob = new Blob([buffer], {
                    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                  });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement("a");
                  link.href = url;
                  link.download = `interpretations-${new Date().toISOString().split("T")[0]}.xlsx`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  URL.revokeObjectURL(url);
                  closeDialog();
                })
                .catch((error) => {
                  console.error("XLSX export error:", error);
                });
            } catch (error) {
              console.error("Export error:", error);
            }
          }}
        >
          XLSX (Excel Export)
        </Button>
      </Stack>
    </Box>
  );

  return (
    <Box
      p="1rem 1rem 0 1rem"
      bgcolor={theme.palette.background.alt}
      sx={{
        flex: 1,
        position: "relative",
        height: `calc(100vh - ${NAVBAR_HEIGHT + 20}px)`,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <Grid container alignItems="center">
          <Grid size={{ xs: 12 }} sx={{ px: 1 }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              width="100%"
              gap={2}
            >
              <Box
                sx={{
                  flex: isMobile ? 0 : 1,
                  maxWidth: isMobile ? undefined : FILTER_SELECTOR_WIDTH,
                }}
              >
                <CustomDateSelector
                  label="Date Range"
                  months={periods.monthsByYear?.[selectedYears?.[0]] || MONTHS}
                  years={
                    availableYears.length > 0 ? availableYears : [currentYear]
                  }
                  selectedMonths={selectedMonths}
                  selectedYears={selectedYears}
                  onSave={handleDateSelectionSave}
                  isMultiMonth={true}
                  isMultiYear={false}
                  monthGridItemSize={3}
                  yearGridItemSize={4}
                  showClearFilter={false}
                  iconOnly={isMobile}
                />
              </Box>
              <Box display="flex" gap={2} alignItems="center" />
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Box
        borderRadius="12px"
        bgcolor={theme.palette.background.default}
        sx={{
          boxShadow: theme.shadows[3],
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          mb: 1,
          mx: 1,
          mt: 2,
          [`.${gridClasses.cell}`]: {
            color: theme.palette.primary.text,
          },
          [`.${gridClasses.cell}.notAvailable`]: {
            color: theme.palette.primary.text3,
          },
        }}
      >
        {isMobile ? (
          <Box sx={{ width: "100%", overflowY: "auto", px: 0.5 }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              Assignments
            </Typography>
            {(columnFilteredData || []).map((row) => (
              <Card
                key={row.id}
                sx={{ mb: 1, borderRadius: 2 }}
                variant="outlined"
              >
                <CardActionArea
                  onClick={() =>
                    navigate(`/linguist-portal/assignments/${row.id}`)
                  }
                >
                  <CardContent sx={{ pb: 1 }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={1}
                    >
                      <Typography variant="subtitle2">
                        {row.ProjectNumber || `#${row.id}`}
                      </Typography>
                      <Typography variant="caption">
                        {row.Date ? dayjs(row.Date).format("M/D/YYYY") : "N/A"}
                      </Typography>
                    </Stack>

                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 700, mt: 0.5 }}
                    >
                      {row.Name || `Assignment #${row.id}`}
                    </Typography>

                    <Box
                      sx={{
                        display: "flex",
                        width: "100%",
                        mt: 0.5,
                        alignItems: "center",
                      }}
                    >
                      <Box sx={{ flex: 1, textAlign: "left" }}>
                        <Typography variant="body2">
                          Start: {formatServiceTime(row.TimeStart)}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1, textAlign: "center" }}>
                        <Typography variant="body2">
                          End: {formatServiceTime(row.TimeEnd)}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: 1, textAlign: "right" }}>
                        <Typography variant="body2">
                          Duration:{" "}
                          {row.Duration && row.Duration !== "00:00:00"
                            ? row.Duration.slice(0, 5)
                            : "N/A"}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ mt: 1 }}>
                      <Chip
                        size="small"
                        label={row.InterpretationStatus || "Assigned"}
                        sx={{
                          fontWeight: 700,
                          backgroundColor: getStatusColor(
                            theme,
                            row.InterpretationStatus,
                          ),
                          color: theme.palette.background.default,
                        }}
                      />
                    </Box>

                    <Typography
                      variant="body2"
                      sx={{ mt: 1, whiteSpace: "pre-wrap" }}
                    >
                      {row.Address || "N/A"}
                    </Typography>
                  </CardContent>
                </CardActionArea>
              </Card>
            ))}
          </Box>
        ) : (
          <DataGrid
            localeText={{
              noRowsLabel:
                "No interpretation projects added yet for this month.",
              noResultsOverlayLabel: "No matches for your filters",
            }}
            pageSizeOptions={[20, 50, 100]}
            getRowId={(row) => row.id}
            rows={columnFilteredData || []}
            filterModel={filterModel}
            onFilterModelChange={handleFilterModelChange}
            disableColumnSorting
            onStateChange={(e) => {
              const lookup = e.filter?.filteredRowsLookup;
              if (!lookup) return;

              const nextIds = Object.keys(lookup).filter((key) => lookup[key]);
              nextIds.sort();

              const prevIds = prevFilteredRowIdsRef.current;
              const isSame =
                prevIds.length === nextIds.length &&
                prevIds.every((id, idx) => id === nextIds[idx]);

              if (!isSame) {
                prevFilteredRowIdsRef.current = nextIds;
                setFilterRowIds(nextIds);
              }
            }}
            sortModel={sortModel}
            onSortModelChange={handleSortModelChange}
            apiRef={apiRef}
            density={density}
            disableRowSelectionOnClick={!!openFilter}
            onRowClick={(params, event) => {
              if (openFilter) {
                setOpenFilter(null);
                return;
              }

              navigate(`/linguist-portal/assignments/${params.id}`);
            }}
            getCellClassName={(params) => {
              return params.value === "No" ||
                params.value === "N/A" ||
                params.value === "0%" ||
                params.value === "null"
                ? "notAvailable"
                : "";
            }}
            slots={{ toolbar: CustomToolbar }}
            columns={filteredColumns}
            slotProps={{
              row: {
                style: { cursor: "context-menu" },
              },
              toolbar: {
                hasAnyActiveFilters,
                onClear: () => clearAllFiltersRef.current(),
                theme,

                showQuickFilter: true,
                printOptions: { disableToolbarButton: true },
                csvOptions: { disableToolbarButton: true },
              },
              loadingOverlay: {
                variant: "skeleton",
                noRowsVariant: "skeleton",
              },
            }}
            ignoreDiacritics
            sx={{
              backgroundColor: theme.palette.background.default,
              borderRadius: "12px",
              height: "100%", // Make DataGrid take full height of container
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: "bold",
                color: theme.palette.primary.main,
              },
              "& .MuiDataGrid-scrollbarFiller": {
                backgroundColor: theme.palette.background.alt,
              },
              "& .MuiDataGrid-sortIcon": {
                display: "none !important",
              },
              "& .MuiDataGrid-columnHeader .MuiDataGrid-iconButtonContainer": {
                display: "none !important",
              },
              "& .MuiDataGrid-menuIconButton": {
                display: "none !important",
              },
              "& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon": {
                display: "none !important",
              },
              "& .MuiDataGrid-columnHeaderTitleContainerContent": {
                flex: 1,
              },
              "& .MuiDataGrid-columnSeparator": {
                color: theme.palette.primary.main,
              },
              "& .MuiButton-root": {
                color: theme.palette.primary.main,
              },
              "& .MuiDataGrid-cell:focus-within, & .MuiDataGrid-cell:focus": {
                outline: "none !important",
              },
              "& .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-columnHeader:focus":
                {
                  outline: "none !important",
                },
            }}
          />
        )}
      </Box>
      {overlayOpen && overlayMode && (
        <Popover
          open={overlayOpen}
          anchorReference="anchorPosition"
          anchorPosition={{
            left: window.innerWidth / 2,
            top: window.innerHeight / 2,
          }}
          onClose={() => setOverlayOpen(false)}
          anchorOrigin={{ vertical: "center", horizontal: "center" }}
          transformOrigin={{ vertical: "center", horizontal: "center" }}
          sx={{
            "& .MuiPopover-paper": {
              transform: "translate(-50%, -50%)",
              borderRadius: "12px",
              boxShadow:
                theme.palette.mode === "dark"
                  ? "0 8px 32px rgba(0,0,0,0.5)"
                  : "0 8px 32px rgba(0,0,0,0.15)",
            },
          }}
        >
          <GridOptionsOverlay />
        </Popover>
      )}
    </Box>
  );
};

export default LinguistInterpretations;
